/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */
import _ from 'lodash';

import { Exemption, type Individu } from '@/types';

// Fonction helper pour copier un champ si vide dans l'individu
const copyIfEmpty = <K extends keyof Individu>(
  updates: Partial<Individu>,
  individu: Individu,
  key: K,
  value: Individu[K],
): void => {
  if (!individu[key] && value !== undefined && value !== null) {
    updates[key] = value;
  }
};

// Fonction helper pour convertir les booléens 0/1 d'un parent
const convertParentBooleans = (parent: any): any => {
  const converted = { ...parent };
  if (converted.scolarise !== undefined) {
    converted.scolarise = converted.scolarise === 1;
  }
  if (converted.activiteProfessionnelle !== undefined) {
    converted.activiteProfessionnelle = converted.activiteProfessionnelle === 1;
  }
  return converted;
};

const parseHubeeDemandeur = (
  teledossierData: any,
  individu: Individu,
): Partial<Individu> => {
  if (!teledossierData?.demandeur) {
    return {};
  }

  const { demandeur } = teledossierData;
  const updates: Partial<Individu> = {};

  // Champs simples - informations personnelles
  copyIfEmpty(updates, individu, 'prenom', demandeur.prenom);
  copyIfEmpty(
    updates,
    individu,
    'prenomsComplementaires',
    demandeur.prenomsComplementaires,
  );
  copyIfEmpty(updates, individu, 'nomFamille', demandeur.nomFamille);
  copyIfEmpty(updates, individu, 'nomUsage', demandeur.nomUsage);
  copyIfEmpty(updates, individu, 'sexe', demandeur.sexe);
  copyIfEmpty(
    updates,
    individu,
    'identifiantNationalite',
    demandeur.identifiantNationalite,
  );

  // Traitement de la double nationalité du demandeur
  if (
    !individu.identifiantNationaliteBis &&
    !_.isNil(demandeur.identifiantNationaliteBis)
  ) {
    updates.identifiantNationaliteBis = demandeur.identifiantNationaliteBis;
    updates.hasDoubleNationalite = true;
  }

  // Date de naissance (copie groupée)
  if (!individu.jourNaissance && demandeur.jourNaissance) {
    updates.jourNaissance = demandeur.jourNaissance;
    updates.moisNaissance = demandeur.moisNaissance;
    updates.anneeNaissance = demandeur.anneeNaissance;
    updates.typeDateNaissance = demandeur.typeDateNaissance;
  }

  // Adresses
  if (
    !individu.adresseNaissance?.libelleCommune &&
    !individu.adresseNaissance?.libellePays &&
    demandeur.adresseNaissance
  ) {
    updates.adresseNaissance = demandeur.adresseNaissance;
  }
  if (
    !individu.adresseRattachement?.libelleVoie &&
    demandeur.adresseRattachement
  ) {
    updates.adresseRattachement = demandeur.adresseRattachement;
  }
  if (!individu.adresseContact?.libelleVoie && demandeur.adresseContact) {
    updates.adresseContact = demandeur.adresseContact;
  }

  // Situation familiale
  if (
    demandeur.situationFamiliale !== undefined &&
    demandeur.situationFamiliale !== null
  ) {
    updates.situationFamiliale = demandeur.situationFamiliale;
  }
  if (demandeur.pupille !== undefined && demandeur.pupille !== null) {
    updates.pupille = demandeur.pupille;
  }
  if (!individu.nombreEnfants && demandeur.nombreEnfants !== undefined) {
    updates.nombreEnfants = demandeur.nombreEnfants;
  }
  if (
    !individu.nombreFreresSoeurs &&
    demandeur.nombreFreresSoeurs !== undefined
  ) {
    updates.nombreFreresSoeurs = demandeur.nombreFreresSoeurs;
  }

  // Exemption et contact
  if (
    demandeur.demandeExemption !== undefined &&
    demandeur.demandeExemption !== null
  ) {
    if (
      demandeur.demandeExemption === Exemption.Invalide ||
      demandeur.demandeExemption === Exemption.Maladie
    ) {
      updates.exemptionActive = true;
    }
    updates.demandeExemption = demandeur.demandeExemption;
  }
  copyIfEmpty(updates, individu, 'tel1', demandeur.tel1);
  copyIfEmpty(updates, individu, 'tel2', demandeur.tel2);
  copyIfEmpty(updates, individu, 'mail', demandeur.mail);

  // Booléens avec conversion 0/1
  if (_.isNil(individu.scolarise) && !_.isNil(demandeur.scolarise)) {
    updates.scolarise = demandeur.scolarise;
  }
  if (
    _.isNil(individu.activiteProfessionnelle) &&
    !_.isNil(demandeur.activiteProfessionnelle)
  ) {
    updates.activiteProfessionnelle = demandeur.activiteProfessionnelle;
  }

  // Parents
  if (!individu.parent1?.nomFamille && demandeur.parent1) {
    updates.parent1 = convertParentBooleans(demandeur.parent1);
  }
  if (!individu.parent2?.nomFamille && demandeur.parent2) {
    updates.parent2 = convertParentBooleans(demandeur.parent2);
  }

  // Filiation inconnue
  if (demandeur.filiationInconnueParent1 !== undefined) {
    updates.filiationInconnueParent1 = demandeur.filiationInconnueParent1 === 1;
  }
  if (demandeur.filiationInconnueParent2 !== undefined) {
    updates.filiationInconnueParent2 = demandeur.filiationInconnueParent2 === 1;
  }

  return updates;
};

export default parseHubeeDemandeur;
